import { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  styled,
  Modal,
  Paper,
  Box,
  IconButton
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import BarChartIcon from '@mui/icons-material/BarChart';
import CloseIcon from '@mui/icons-material/Close';

import THEME from '../../../constants/theme';

const StyledCard = styled(Card)(() => ({
  height: '80%',
  display: 'flex',
  margin: '1rem 0',
  flexDirection: 'column',
  transition: 'transform 0.3s, box-shadow 0.3s',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: THEME.SHADOWS.LARGE,
    '& .icon': {
      color: THEME.COLORS.PRIMARY_BLUE,
      transform: 'translateX(5px)'
    }
  }
}));

const CardTitle = styled(Typography)({
  fontWeight: 600,
  marginBottom: '0',
  color: THEME.COLORS.PRIMARY_BLUE,
  fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY
});

const CardDescription = styled(Typography)({
  flexGrow: 1,
  fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY
});

const IconStyled = styled('div')({
  transition: 'transform 0.3s, color 0.3s',
  marginTop: '10px',
  display: 'flex',
  alignItems: 'center',
  gap: '5px'
});

const StyledModal = styled(Modal)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}));

const ModalContent = styled(Paper)(() => ({
  backgroundColor: THEME.COLORS.NEUTRAL_WHITE,
  boxShadow: THEME.SHADOWS.LARGE,
  padding: THEME.SPACING.XL,
  outline: 'none',
  maxWidth: '80%',
  maxHeight: '90%',
  overflow: 'auto',
  position: 'relative',
  borderRadius: '8px'
}));

const CloseButton = styled(IconButton)(() => ({
  position: 'absolute',
  right: THEME.SPACING.MEDIUM,
  top: THEME.SPACING.MEDIUM,
  color: THEME.COLORS.NEUTRAL_GRAY,
  '&:hover': {
    color: THEME.COLORS.NEUTRAL_BLACK,
    backgroundColor: 'rgba(0,0,0,0.05)'
  }
}));

/**
 * EnhancedLegalCard component - Card specifically for legal documents with modal support
 *
 * @param {Object} props - Component props
 * @param {string} props.title - Card title
 * @param {string} props.description - Card description
 * @param {boolean} props.isPdf - Whether the document is a PDF
 * @param {string} props.pdfUrl - URL to PDF document
 * @param {boolean} props.hasModal - Whether the card opens a modal
 * @param {string} props.modalTitle - Title for the modal
 * @param {string} props.modalContent - Text content for the modal
 * @param {string} props.modalImage - Image to display in the modal
 * @param {function} props.onClick - Function to execute on card click
 * @returns {React.Component} EnhancedLegalCard component
 */
const LegalCard = ({
  title,
  description,
  isPdf = false,
  pdfUrl,
  hasModal = false,
  modalTitle,
  modalContent,
  modalImage,
  onClick
}) => {
  // Modal state
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  // Handle card click
  const handleCardClick = () => {
    if (onClick) {
      onClick();
    } else if (pdfUrl) {
      window.open(pdfUrl, '_blank');
    } else if (hasModal) {
      handleOpen();
    }
  };

  // Import modal image dynamically from assets
  let modalImageSrc;
  if (modalImage) {
    // Check if it's an external URL (starts with http:// or https://)
    if (modalImage.startsWith('http://') || modalImage.startsWith('https://')) {
      modalImageSrc = modalImage;
    } else {
      try {
        // Attempt to import modal image from assets
        modalImageSrc = require(`../../../assets/images${modalImage}`);
      } catch (error) {
        // Fallback to placeholder if image not found
        modalImageSrc =
          'https://via.placeholder.com/1000x600?text=Image+Not+Found';
        // Image not found - using fallback placeholder
      }
    }
  }

  return (
    <>
      <StyledCard onClick={handleCardClick}>
        <CardContent>
          <CardTitle variant='h6'>{title}</CardTitle>
          <CardDescription variant='body2'>{description}</CardDescription>
          <IconStyled>
            {isPdf ? (
              <PictureAsPdfIcon className='icon' />
            ) : hasModal ? (
              <BarChartIcon className='icon' />
            ) : (
              <ArrowForwardIcon className='icon' />
            )}
          </IconStyled>
        </CardContent>
      </StyledCard>

      {/* Modal for displaying insights and graphs */}
      {hasModal && (
        <StyledModal
          open={open}
          onClose={handleClose}
          aria-labelledby='modal-title'
          aria-describedby='modal-description'
        >
          <ModalContent>
            <CloseButton onClick={handleClose}>
              <CloseIcon />
            </CloseButton>

            <Typography
              id='modal-title'
              variant='h4'
              component='h2'
              sx={{
                mb: 3,
                color: THEME.COLORS.PRIMARY_BLUE,
                fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.SEMI_BOLD
              }}
            >
              {modalTitle || title}
            </Typography>

            <Typography
              id='modal-description'
              variant='body1'
              sx={{
                mb: 4,
                fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
                lineHeight: 1.7
              }}
            >
              {modalContent}
            </Typography>

            {modalImageSrc && (
              <Box
                component='img'
                src={modalImageSrc}
                alt='Graph or chart'
                sx={{
                  width: '100%',
                  maxHeight: '500px',
                  objectFit: 'contain',
                  borderRadius: '4px',
                  mb: 2
                }}
              />
            )}
          </ModalContent>
        </StyledModal>
      )}
    </>
  );
};

export default LegalCard;
